import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { OnboardingProvider } from "@/components/onboarding/OnboardingProvider";
import { PWAProvider } from "@/components/pwa";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import TimerPage from "./pages/TimerPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import ClientsPage from "./pages/ClientsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import PaymentsPage from "./pages/PaymentsPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import CalculatorPage from "./pages/CalculatorPage";
import NotFound from "./pages/NotFound";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import RefundPage from "./pages/RefundPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on AbortError or auth errors
        const err = error as Error;
        if (err?.name === 'AbortError') return false;
        if (err?.message?.includes('JWT')) return false;
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
    mutations: {
      retry: false,
    },
  },
});

// Component to handle global unhandled promise rejections
function GlobalErrorHandler({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const errorName = reason?.name || '';
      const errorMessage = reason?.message || '';
      
      // List of error patterns that should be silently handled
      // These are typically transient network issues or browser-level aborts
      const silentErrorPatterns = [
        'FunctionsFetchError',
        'FunctionsHttpError',
        'FunctionsRelayError',
        'Failed to send a request',
        'Edge Function',
        'check-subscription',
        'NetworkError',
        'fetch failed',
        'Failed to fetch',
        'AbortError',
        'signal is aborted',
        'The operation was aborted',
        'aborted without reason',
        'The user aborted a request',
        'Load failed',
        'cancelled',
        'net::ERR_',
      ];
      
      const shouldSilence = silentErrorPatterns.some(
        pattern => errorName.includes(pattern) || errorMessage.includes(pattern)
      );
      
      if (shouldSilence) {
        // Log at debug level to avoid polluting the console
        console.debug("[GlobalErrorHandler] Silenced transient error:", errorName, errorMessage);
        event.preventDefault();
        return;
      }
      
      // For other errors, also prevent default and log for debugging
      console.warn("[GlobalErrorHandler] Unhandled promise rejection:", reason);
      event.preventDefault();
    };

    window.addEventListener("unhandledrejection", handleRejection);
    return () => window.removeEventListener("unhandledrejection", handleRejection);
  }, []);

  return <>{children}</>;
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <GlobalErrorHandler>
          <ErrorBoundary>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AuthProvider>
                <PWAProvider>
                  <OnboardingProvider>
                    <Routes>
                      {/* Rotas públicas */}
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/signup" element={<SignupPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      <Route path="/reset-password" element={<ResetPasswordPage />} />
                      <Route path="/termos" element={<TermsPage />} />
                      <Route path="/privacidade" element={<PrivacyPage />} />
                      <Route path="/cancelamento" element={<RefundPage />} />

                      {/* Rotas protegidas - requer autenticação */}
                      <Route element={<ProtectedRoute />}>
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/timer" element={<TimerPage />} />
                        <Route path="/projects" element={<ProjectsPage />} />
                        <Route path="/projects/:id" element={<ProjectDetailPage />} />
                        <Route path="/clients" element={<ClientsPage />} />
                        <Route path="/clients/:id" element={<ClientDetailPage />} />
                        <Route path="/payments" element={<PaymentsPage />} />
                        <Route path="/calculator" element={<CalculatorPage />} />
                        <Route path="/reports" element={<ReportsPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                      </Route>

                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </OnboardingProvider>
                </PWAProvider>
              </AuthProvider>
            </BrowserRouter>
          </ErrorBoundary>
        </GlobalErrorHandler>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
